/**
 * Everything around the Grokbot pages that is not a page: the toolbar with its
 * tools, the command palette, keyboard shortcuts and help, saved views,
 * notification preferences and the theme. One component so the palette's
 * commands and the toolbar's buttons open the same dialogs.
 */

import React from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Snackbar, Switch, TextField, Typography } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createAppTheme } from '../../theme/muiTheme';
import { createView, deleteView, getViews, requestSync } from './api/botOpsService';
import { BotOpsHttpError } from './api/botOpsClient';
import { useBotOpsData } from './BotOpsData';
import { BotOpsToolbar, ToolButton } from './BotOpsToolbar';
import { CommandPalette } from './CommandPalette';
import { ShortcutsHelp } from './ShortcutsHelp';
import { usePrefs } from './hooks/usePrefs';
import { useQuery } from './hooks/useQuery';
import { CommandId, PaletteItem } from './lib/palette';
import { GO_WINDOW_MS, interpretKey, isTypingTarget } from './lib/shortcuts';
import { moveRow, pressInRow, toggleSelectInRow } from './lib/rowKeys';
import { DARK_PRIMARY, FONT, OVERLINE, RADIUS, TEXT_SECONDARY_ON_DARK } from './lib/brand';

const BASE = '/dashboard/bot-ops';
type Mode = 'light' | 'dark';

const systemMode = (): Mode => {
  try { return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'; } catch { return 'light'; }
};

/** ?theme= wins, then the saved preference, then the system setting. */
export function resolveMode(urlTheme: string | null, saved: Mode | null | undefined, system: Mode): Mode {
  if (urlTheme === 'dark' || urlTheme === 'light') return urlTheme;
  return saved === 'dark' || saved === 'light' ? saved : system;
}

export function useBotOpsMode(): Mode {
  const [sp] = useSearchParams();
  const { prefs } = usePrefs();
  return resolveMode(sp.get('theme'), prefs ? prefs.theme : null, systemMode());
}

/** Nests the app theme in the chosen mode around the Grokbot pages; the dark page gets its own background. */
export function ThemeShell({ children }: { children: React.ReactNode }) {
  const mode = useBotOpsMode();
  const outer = useTheme();
  // In the dark theme the brand teal is too dark to read as text or a link; use a lighter tint with dark text on filled controls.
  const theme = React.useMemo(() => {
    const base = outer.palette.mode === mode ? outer : createAppTheme(mode);
    return mode === 'dark' ? createTheme(base, { palette: { primary: DARK_PRIMARY, text: { secondary: TEXT_SECONDARY_ON_DARK } } }) : base;
  }, [mode, outer]);
  return (
    <ThemeProvider theme={theme}>
      <Box data-bot-ops-mode={mode} sx={{ color: 'text.primary', '& a:not([class])': { color: 'primary.main' }, ...(mode === 'dark' ? { bgcolor: 'background.default', minHeight: 'calc(100vh - 64px)' } : {}) }}>
        {children}
      </Box>
    </ThemeProvider>
  );
}

function SavedViewsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const q = useQuery(getViews, []);
  const [name, setName] = React.useState('');
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const here = `${loc.pathname}${loc.search}`;
  const save = async () => {
    setBusy(true); setErr(null);
    try { await createView({ name: name.trim(), query: here }); setName(''); q.reload(); } catch (e) { setErr(e instanceof BotOpsHttpError ? e.message : 'Could not save the view'); } finally { setBusy(false); }
  };
  const remove = async (id: string) => {
    setErr(null);
    try { await deleteView(id); q.reload(); } catch (e) { setErr(e instanceof BotOpsHttpError ? e.message : 'Could not delete the view'); }
  };
  const list = q.data || [];
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="views-title" data-testid="views-dialog">
      <DialogTitle id="views-title" sx={{ fontFamily: FONT.heading, fontSize: 16, fontWeight: 700 }}>Saved views</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.5 }}>
          <TextField size="small" fullWidth label="Name this view" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 80 }} helperText={`Saves this page: ${here}`} />
          <Button variant="contained" onClick={save} disabled={busy || !name.trim()} sx={{ textTransform: 'none', mt: 0.2 }}>Save</Button>
        </Box>
        {err && <Alert severity="error" role="alert" sx={{ my: 1 }}>{err}</Alert>}
        {q.error && !q.data && <Alert severity="error" role="alert" sx={{ my: 1 }} action={<Button size="small" color="inherit" onClick={q.reload}>Retry</Button>}>Could not load your views.</Alert>}
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5 }}>
          {list.map((v) => (
            <Box component="li" key={v.id} data-testid="view-row" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.8, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button onClick={() => { onClose(); navigate(v.query); }} sx={{ textTransform: 'none', justifyContent: 'flex-start', flex: 1, minWidth: 0 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>{v.name}</Box>
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.query}</Typography>
              <IconButton size="small" aria-label={`Delete view ${v.name}`} onClick={() => remove(v.id)}>✕</IconButton>
            </Box>
          ))}
          {q.data && list.length === 0 && <Typography variant="body2" color="text.secondary" data-testid="views-empty">No saved views yet.</Typography>}
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function PrefsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { prefs, update, error } = usePrefs();
  const n = prefs ? prefs.notify : null;
  const sw = (key: 'unread_badges' | 'digest' | 'idle_alerts', label: string, help: string) => (
    <Box>
      <FormControlLabel
        control={<Switch checked={n ? n[key] !== false : true} disabled={!n} onChange={(e) => update({ notify: { [key]: e.target.checked } })} />}
        label={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{label}</Typography>}
      />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 4.5, mt: -0.5, mb: 0.8 }}>{help}</Typography>
    </Box>
  );
  const future = (label: string) => (
    <Box>
      <FormControlLabel control={<Switch disabled checked={false} />} label={<Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>{label} <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, px: 0.6, borderRadius: RADIUS.sm, bgcolor: 'action.selected' }}>FUTURE · DRAFT-ONLY</Box></Typography>} />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 4.5, mt: -0.5, mb: 0.8 }}>Would write a draft for you to approve. Nothing is sent without your approval, and this connector is not switched on.</Typography>
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="prefs-title" data-testid="prefs-dialog">
      <DialogTitle id="prefs-title" sx={{ fontFamily: FONT.heading, fontSize: 16, fontWeight: 700 }}>Notification preferences</DialogTitle>
      <DialogContent dividers>
        <Typography variant="overline" sx={{ ...OVERLINE }}>In this app</Typography>
        {sw('unread_badges', 'Unread-reply badges', 'The red counters on bots and items when a bot has replied.')}
        {sw('digest', 'Since-you-last-looked digest', 'The summary strip at the top of Operations.')}
        {sw('idle_alerts', 'Idle alerts', 'Idle warnings on a bot’s page and in the digest.')}
        <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', mt: 1.5 }}>Elsewhere</Typography>
        {future('Slack digest')}
        {future('Email digest')}
        {!prefs && <Alert severity="warning" sx={{ mt: 1 }}>Preferences have not loaded yet.</Alert>}
        {error && <Alert severity="error" role="alert" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={onClose} autoFocus>Done</Button></DialogActions>
    </Dialog>
  );
}

/** The toolbar, palette, shortcuts and dialogs for every Grokbot page. */
export function BotOpsChrome() {
  const navigate = useNavigate();
  const loc = useLocation();
  const [sp, setSp] = useSearchParams();
  const data = useBotOpsData();
  const { prefs, update } = usePrefs();
  const mode = useBotOpsMode();
  const [palette, setPalette] = React.useState(false);
  const [help, setHelp] = React.useState(false);
  const [views, setViews] = React.useState(false);
  const [prefsOpen, setPrefsOpen] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);
  const goRef = React.useRef<number | null>(null);
  const pendingGo = React.useRef(false);
  const modeRef = React.useRef(mode); modeRef.current = mode;

  const toggleTheme = React.useCallback(async () => {
    const next: Mode = modeRef.current === 'dark' ? 'light' : 'dark';
    // An explicit ?theme= would keep winning over the saved choice, so a toggle drops it.
    if (sp.get('theme')) { const n = new URLSearchParams(sp); n.delete('theme'); setSp(n, { replace: true }); }
    if (!(await update({ theme: next }))) setToast('Could not save your theme choice. It applies until you reload.');
  }, [sp, setSp, update]);

  const syncNow = React.useCallback(async () => {
    try {
      await requestSync();
      setToast('Sync requested. New data shows when Grok Bot answers.');
      await data.refresh();
    } catch (e) {
      setToast(e instanceof BotOpsHttpError && e.status === 429 ? `One sync every 30 s. Try again in ${e.retryAfter || 30} s.` : e instanceof BotOpsHttpError && e.status === 403 ? 'Only the super admin can request a sync.' : 'Could not request a sync.');
    }
  }, [data]);

  const runCommand = React.useCallback((c: CommandId) => {
    if (c === 'sync') void syncNow();
    else if (c === 'theme') void toggleTheme();
    else if (c === 'prefs') setPrefsOpen(true);
    else if (c === 'views') setViews(true);
    else setHelp(true);
  }, [syncNow, toggleTheme]);

  const run = React.useCallback((it: PaletteItem) => {
    if (it.command) runCommand(it.command);
    else if (it.to) navigate(it.to);
  }, [navigate, runCommand]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const modalOpen = Boolean(document.querySelector('[role="dialog"], [role="presentation"].MuiDialog-root, .MuiDrawer-root'));
      const r = interpretKey(e, { typing: isTypingTarget(e.target), modalOpen, pendingGo: pendingGo.current });
      if (goRef.current) { window.clearTimeout(goRef.current); goRef.current = null; }
      pendingGo.current = r.pendingGo;
      if (r.pendingGo) goRef.current = window.setTimeout(() => { pendingGo.current = false; }, GO_WINDOW_MS);
      if (!r.action) return;
      if (r.preventDefault) e.preventDefault();
      switch (r.action) {
        case 'palette': setPalette(true); break;
        case 'help': setHelp(true); break;
        case 'next': moveRow(1); break;
        case 'prev': moveRow(-1); break;
        case 'yes': pressInRow('Yes'); break;
        case 'no': pressInRow('No'); break;
        case 'select': toggleSelectInRow(); break;
        case 'instruct':
          if (!pressInRow('Instruct instead') && /\/bots\/[^/]+/.test(loc.pathname)) {
            const n = new URLSearchParams(sp); n.set('tab', 'inbox'); n.set('compose', '1'); setSp(n);
          }
          break;
        case 'go-operations': navigate(BASE); break;
        case 'go-activity': navigate(`${BASE}/activity`); break;
        case 'go-scorecard': navigate(`${BASE}/scorecard`); break;
        case 'go-audit': navigate(`${BASE}/audit`); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); if (goRef.current) window.clearTimeout(goRef.current); };
  }, [navigate, loc.pathname, sp, setSp]);

  return (
    <>
      <BotOpsToolbar
        tools={(
          <>
            <ToolButton onClick={() => setPalette(true)} aria-keyshortcuts="Control+K Meta+K /">Search <Box component="span" aria-hidden="true" sx={{ ml: 0.6, fontSize: 11 }}>⌘K</Box></ToolButton>
            <ToolButton onClick={() => setViews(true)}>☆ Views</ToolButton>
            <ToolButton onClick={() => setPrefsOpen(true)}>Preferences</ToolButton>
            <ToolButton onClick={toggleTheme} aria-label={mode === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'} data-testid="theme-toggle">{mode === 'dark' ? '☀' : '☾'}</ToolButton>
            <ToolButton onClick={() => setHelp(true)} aria-label="Keyboard shortcuts">?</ToolButton>
          </>
        )}
      />
      <CommandPalette open={palette} onClose={() => setPalette(false)} snapshot={data.data} onRun={run} />
      <ShortcutsHelp open={help} onClose={() => setHelp(false)} />
      {views && <SavedViewsDialog open onClose={() => setViews(false)} />}{/* mounted only while open, so the list is fetched when asked for */}
      <PrefsDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" variant="filled" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
